import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, User, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useCanonical(slug ? `https://ezeefitofficesystems.in/blog/${slug}` : null);
  useMetaDescription(
    blog
      ? (blog.meta_description
          || (blog.content
              ? blog.content.replace(/[#*`_>\-]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 155) + '…'
              : `${blog.title} — expert office furniture insights by Ezeefit Office Systems, Mumbai modular furniture manufacturer since 2014.`))
      : null
  );

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await axios.get(`${API}/blogs/${slug}`);
        setBlog(response.data);
        
        // Fetch related blogs
        const allBlogsResponse = await axios.get(`${API}/blogs`);
        const related = allBlogsResponse.data
          .filter(b => b.slug !== slug)
          .filter(b => b.category === response.data.category)
          .slice(0, 3);
        
        // If not enough related by category, add more recent blogs
        if (related.length < 3) {
          const moreBlogs = allBlogsResponse.data
            .filter(b => b.slug !== slug && !related.find(r => r.slug === b.slug))
            .slice(0, 3 - related.length);
          related.push(...moreBlogs);
        }
        
        setRelatedBlogs(related);
      } catch (error) {
        console.error("Error fetching blog:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Article Not Found</h2>
          <Button asChild>
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="blog-detail-page">
      <Helmet title={`${blog.title || "Blog"} | Ezeefit Office Systems Blog`}>
        <meta name="description" content={blog.meta_description || (blog.content ? blog.content.replace(/[#*`_>\-]/g,' ').replace(/\s+/g,' ').trim().slice(0, 155) + '…' : `${blog.title} — expert office furniture insights by Ezeefit Office Systems, Mumbai modular furniture manufacturer since 2014.`)} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${blog.title} | Ezeefit Blog`} />
        <meta property="og:description" content={blog.meta_description || (blog.content ? blog.content.replace(/[#*`_>\-]/g,' ').replace(/\s+/g,' ').trim().slice(0, 155) + '…' : blog.title)} />
        <meta property="og:url" content={`https://ezeefitofficesystems.in/blog/${slug}`} />
        <meta property="og:image" content={blog.image} />
        <meta property="article:published_time" content={blog.published_at || blog.created_at} />
        <meta property="article:section" content={blog.category || 'Office Furniture'} />
      </Helmet>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blog.title,
            "description": blog.meta_description || (blog.content ? blog.content.replace(/[#*`_>\-]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 155) : blog.title),
            "image": blog.image,
            "datePublished": blog.published_at || blog.created_at,
            "dateModified": blog.updated_at || blog.published_at || blog.created_at,
            "author": { "@type": "Organization", "name": "Ezeefit Office Systems" },
            "publisher": {
              "@type": "Organization",
              "name": "Ezeefit Office Systems",
              "legalName": "Ezeefit Modular Furniture Private Limited",
              "logo": { "@type": "ImageObject", "url": "https://ezeefitofficesystems.in/favicon-512x512.png" }
            },
            "mainEntityOfPage": { "@type": "WebPage", "@id": `https://ezeefitofficesystems.in/blog/${slug}` }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in/" },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://ezeefitofficesystems.in/blog" },
              { "@type": "ListItem", "position": 3, "name": blog.title, "item": `https://ezeefitofficesystems.in/blog/${slug}` }
            ]
          })
        }}
      />
      {/* Hero */}
      <section className="relative py-24 bg-[#0F172A] noise-overlay overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={blog.image}
            alt={blog.title}
            className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Blog
            </Link>

            <span className="inline-block bg-[#F97316] text-white text-sm font-mono px-4 py-2 uppercase tracking-wider mb-6">
              {blog.category}
            </span>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              {blog.title}
            </h1>

            <div className="flex flex-wrap gap-6 text-[#94A3B8]">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {blog.author}
              </div>
              <div className="flex items-center gap-2">
                <Tag className="w-5 h-5" />
                {blog.published_at}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {blog.read_time}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="prose prose-lg max-w-none"
              >
                <p className="text-xl text-[#334155] leading-relaxed mb-8">
                  {blog.excerpt}
                </p>

                <div className="w-full h-px bg-[#E2E8F0] my-8" />

                {/* Render HTML content if it contains tags, otherwise plain text */}
                {blog.content && blog.content.includes('<') ? (
                  <div 
                    className="text-[#334155] leading-relaxed blog-content"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                  />
                ) : (
                  <div className="text-[#334155] leading-relaxed space-y-6">
                    <p>{blog.content}</p>
                  </div>
                )}
              </motion.div>

              {/* CTA Box */}
              <div className="mt-16 p-8 bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white">
                <h3 className="text-2xl font-bold mb-3">
                  Planning Your Office Workspace?
                </h3>
                <p className="text-[#94A3B8] mb-6">
                  Talk to the workspace experts at Ezeefit Office Systems. We design, manufacture and install modular office furniture for corporate workspaces.
                </p>
                <Button
                  asChild
                  className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-4 font-semibold uppercase tracking-wider"
                >
                  <Link to="/contact">Request Workspace Consultation</Link>
                </Button>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Related Blogs */}
              {relatedBlogs.length > 0 && (
                <div className="sticky top-24">
                  <h3 className="text-lg font-bold text-[#0F172A] mb-6 pb-3 border-b border-[#E2E8F0]">
                    Related Articles
                  </h3>
                  <div className="space-y-6">
                    {relatedBlogs.map((relatedBlog) => (
                      <Link
                        key={relatedBlog.slug}
                        to={`/blog/${relatedBlog.slug}`}
                        className="group block"
                      >
                        <div className="flex gap-4">
                          <div className="w-20 h-20 shrink-0 overflow-hidden">
                            <img src={relatedBlog.image}
                              alt={relatedBlog.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" decoding="async" />
                          </div>
                          <div>
                            <span className="text-xs text-[#F97316] font-mono uppercase">
                              {relatedBlog.category}
                            </span>
                            <h4 className="text-sm font-semibold text-[#0F172A] group-hover:text-[#F97316] transition-colors line-clamp-2 mt-1">
                              {relatedBlog.title}
                            </h4>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Quick Links */}
                  <div className="mt-10 pt-6 border-t border-[#E2E8F0]">
                    <h3 className="text-lg font-bold text-[#0F172A] mb-4">
                      Quick Links
                    </h3>
                    <div className="space-y-3">
                      <Link to="/case-studies" className="flex items-center gap-2 text-[#64748B] hover:text-[#F97316] transition-colors">
                        <ArrowRight className="w-4 h-4" />
                        View Case Studies
                      </Link>
                      <Link to="/solutions" className="flex items-center gap-2 text-[#64748B] hover:text-[#F97316] transition-colors">
                        <ArrowRight className="w-4 h-4" />
                        Industry Solutions
                      </Link>
                      <Link to="/manufacturing" className="flex items-center gap-2 text-[#64748B] hover:text-[#F97316] transition-colors">
                        <ArrowRight className="w-4 h-4" />
                        Our Manufacturing
                      </Link>
                      <Link to="/contact" className="flex items-center gap-2 text-[#64748B] hover:text-[#F97316] transition-colors">
                        <ArrowRight className="w-4 h-4" />
                        Contact Us
                      </Link>
                    </div>
                  </div>

                  {/* Newsletter CTA */}
                  <div className="mt-10 p-6 bg-[#F8FAFC] border border-[#E2E8F0]">
                    <h4 className="font-bold text-[#0F172A] mb-2">
                      Need Expert Advice?
                    </h4>
                    <p className="text-sm text-[#64748B] mb-4">
                      Get personalized workspace planning consultation from our experts.
                    </p>
                    <Button asChild size="sm" className="w-full bg-[#0F172A]">
                      <Link to="/contact">Get Free Consultation</Link>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
